/**
 * IN-CARD SLIDER KIT (2026-09-28, motion board items 1 + 2).
 *
 * Turns a native swipe row into the reference's slider on the phone. The row
 * keeps its markup and its no-JS behaviour (overflow-x + scroll-snap); this
 * file only takes over while the row's own media query in data-d2-kit matches.
 * Measured on rhodeskin.com at 390 (Swiper, E1 ingredients / E5 product cards,
 * docs/调研/卡片骨架对标_Rhode_2026-09-27/rhode_motion/):
 *
 *   frame     the row is clipped to its padding box with 12px corners, so a
 *             peeking slide is cut inside the card, never at the screen edge
 *   gap       20 between slides
 *   gesture   follows the finger; a swipe shorter than 300ms moves exactly one
 *             slide whatever its length; a longer drag moves to the nearest
 *             slide (half-way rule); no momentum; past either end the row
 *             gives only distance^0.85 and springs back
 *   landing   300ms, CSS `ease`
 *   dots      one per resting position, 20 below the slides, 8px with the
 *             current one at 12, 8 apart
 *   badge     (data-d2-kit-badge) a 75px frosted "swipe" disc at 74% / 50% of
 *             the frame that fades over .5s on the first touch and stays gone
 *
 * Widths are NOT unified: each row keeps the slide width Leo set for it (the
 * guide's 78% is his 8-21 call). Resting positions are the scroll offsets that
 * put a slide on the frame's left edge, the last one clamped to the end.
 *
 * Scroll-based rather than transform-based: the row is still a scroll
 * container, only its user scrolling is switched off, so the no-JS path and the
 * desktop grids need no second layout.
 */
(() => {
  const rows = Array.from(document.querySelectorAll('[data-d2-kit]'));
  if (!rows.length) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SPEED = 300;
  const SHORT_MS = 300;
  const RESIST = 0.85;
  const LOCK_PX = 6;

  // CSS `ease` = cubic-bezier(.25, .1, .25, 1), solved for y at a given x.
  const ease = (x) => {
    const p1x = 0.25, p1y = 0.1, p2x = 0.25, p2y = 1;
    let lo = 0, hi = 1, t = x;
    for (let i = 0; i < 24; i++) {
      t = (lo + hi) / 2;
      const bx = 3 * (1 - t) * (1 - t) * t * p1x + 3 * (1 - t) * t * t * p2x + t * t * t;
      if (bx < x) lo = t; else hi = t;
    }
    return 3 * (1 - t) * (1 - t) * t * p1y + 3 * (1 - t) * t * t * p2y + t * t * t;
  };

  const setup = (row) => {
    const query = window.matchMedia(row.getAttribute('data-d2-kit') || '(max-width: 767px)');
    const wantsBadge = row.hasAttribute('data-d2-kit-badge');
    let on = false;
    let snaps = [0];
    let index = 0;
    let anim = 0;
    let dots = null;
    let badge = null;
    let badgeGone = false;
    let drag = null;
    let swallowClick = false;
    let resizeObs = null;

    const slides = () => Array.from(row.children).filter((el) => !el.classList.contains('d2-kit-badge'));

    const measure = () => {
      const cs = getComputedStyle(row);
      const padL = parseFloat(cs.paddingLeft) || 0;
      const rowLeft = row.getBoundingClientRect().left;
      const max = Math.max(0, row.scrollWidth - row.clientWidth);
      const list = [];
      slides().forEach((el) => {
        const x = Math.min(max, Math.max(0, el.getBoundingClientRect().left - rowLeft + row.scrollLeft - padL));
        if (!list.length || Math.abs(x - list[list.length - 1]) > 1) list.push(Math.round(x));
      });
      if (!list.length) list.push(0);
      if (max - list[list.length - 1] > 1) list.push(Math.round(max));
      snaps = list;
      row.style.setProperty('--kit-clip', `inset(${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft} round 12px)`);
      renderDots();
    };

    const renderDots = () => {
      if (!dots) return;
      if (dots.children.length !== snaps.length) {
        dots.textContent = '';
        snaps.forEach((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'd2-kit-dot';
          b.setAttribute('aria-label', `Show slide ${i + 1} of ${snaps.length}`);
          b.addEventListener('click', () => {
            hideBadge();
            goTo(i);
          });
          dots.appendChild(b);
        });
      }
      dots.hidden = snaps.length < 2;
      Array.from(dots.children).forEach((b, i) => {
        b.classList.toggle('is-on', i === index);
        b.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    };

    const setOver = (px) => {
      row.style.setProperty('--kit-over', `${px}px`);
    };

    const goTo = (i, instant) => {
      cancelAnimationFrame(anim);
      index = Math.max(0, Math.min(snaps.length - 1, i));
      renderDots();
      const from = row.scrollLeft;
      const to = snaps[index];
      const overFrom = parseFloat(row.style.getPropertyValue('--kit-over')) || 0;
      if (instant || reduce || (Math.abs(to - from) < 0.5 && Math.abs(overFrom) < 0.5)) {
        row.scrollLeft = to;
        setOver(0);
        return;
      }
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / SPEED);
        const e = ease(p);
        row.scrollLeft = p < 1 ? from + (to - from) * e : to;
        setOver(p < 1 ? overFrom * (1 - e) : 0);
        if (p < 1) anim = requestAnimationFrame(step);
      };
      anim = requestAnimationFrame(step);
    };

    const nearest = (x) => {
      let best = 0;
      snaps.forEach((s, i) => {
        if (Math.abs(s - x) < Math.abs(snaps[best] - x)) best = i;
      });
      return best;
    };

    const hideBadge = () => {
      if (!badge || badgeGone) return;
      badgeGone = true;
      badge.classList.add('is-gone');
    };

    const onDown = (e) => {
      if (!on || (e.pointerType === 'mouse' && e.button !== 0)) return;
      cancelAnimationFrame(anim);
      drag = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        t: performance.now(),
        start: row.scrollLeft,
        startIndex: index,
        axis: null,
        dx: 0,
      };
      swallowClick = false;
    };

    const onMove = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      if (!drag.axis) {
        if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.axis === 'y') {
          drag = null;
          return;
        }
        // Timed from the moment the gesture is known to be a swipe, so a slow
        // start before the lock does not turn a flick into a drag.
        drag.t = performance.now();
        drag.x = e.clientX;
        drag.start = row.scrollLeft;
        hideBadge();
        try { row.setPointerCapture(e.pointerId); } catch (err) { /* already gone */ }
        return;
      }
      e.preventDefault();
      drag.dx = e.clientX - drag.x;
      if (Math.abs(drag.dx) > 3) swallowClick = true;
      const max = snaps[snaps.length - 1];
      const want = drag.start - drag.dx;
      if (want < 0) {
        row.scrollLeft = 0;
        setOver(Math.pow(-want, RESIST));
      } else if (want > max) {
        row.scrollLeft = max;
        setOver(-Math.pow(want - max, RESIST));
      } else {
        row.scrollLeft = want;
        setOver(0);
      }
    };

    const onUp = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const d = drag;
      drag = null;
      if (d.axis !== 'x') return;
      const quick = performance.now() - d.t < SHORT_MS;
      if (quick && Math.abs(d.dx) > 0) {
        goTo(d.startIndex + (d.dx < 0 ? 1 : -1));
      } else {
        goTo(nearest(d.start - d.dx));
      }
    };

    const onCancel = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const d = drag;
      drag = null;
      if (d.axis === 'x') goTo(nearest(row.scrollLeft));
    };

    const onClick = (e) => {
      if (!swallowClick) return;
      swallowClick = false;
      e.preventDefault();
      e.stopPropagation();
    };

    const onFocus = (e) => {
      if (!on || drag) return;
      const slide = slides().find((el) => el.contains(e.target));
      if (!slide) return;
      const rowLeft = row.getBoundingClientRect().left;
      const padL = parseFloat(getComputedStyle(row).paddingLeft) || 0;
      goTo(nearest(slide.getBoundingClientRect().left - rowLeft + row.scrollLeft - padL));
    };

    const stopImgDrag = (e) => {
      if (on) e.preventDefault();
    };

    const enable = () => {
      if (on) return;
      on = true;
      row.classList.add('is-kit');
      if (!dots) {
        dots = document.createElement('div');
        dots.className = 'd2-kit-dots';
        dots.setAttribute('role', 'group');
        dots.setAttribute('aria-label', 'Slides');
        row.insertAdjacentElement('afterend', dots);
      }
      if (wantsBadge && !badge && !badgeGone) {
        badge = document.createElement('span');
        badge.className = 'd2-kit-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = 'swipe';
        row.appendChild(badge);
      }
      if (badge) badge.style.removeProperty('display');
      // Dots sit 20 under the slides. The row's own bottom padding is already
      // below them, so the dots are pulled up into it, and whatever the padding
      // had left over stays under the dots: the next block does not move.
      const pb = parseFloat(getComputedStyle(row).paddingBottom) || 0;
      dots.style.setProperty('--kit-dots-top', `${20 - pb}px`);
      dots.style.setProperty('--kit-after', `${Math.max(0, pb - 32)}px`);
      measure();
      index = nearest(row.scrollLeft);
      goTo(index, true);
      row.addEventListener('pointerdown', onDown);
      row.addEventListener('pointermove', onMove, { passive: false });
      row.addEventListener('pointerup', onUp);
      row.addEventListener('pointercancel', onCancel);
      row.addEventListener('click', onClick, true);
      row.addEventListener('focusin', onFocus);
      row.addEventListener('dragstart', stopImgDrag);
      if ('ResizeObserver' in window) {
        resizeObs = new ResizeObserver(() => {
          if (!on || drag) return;
          measure();
          goTo(index, true);
        });
        resizeObs.observe(row);
      }
    };

    const disable = () => {
      if (!on) return;
      on = false;
      cancelAnimationFrame(anim);
      drag = null;
      row.classList.remove('is-kit');
      row.style.removeProperty('--kit-clip');
      // Without .is-kit the row is no longer the disc's positioning box, so a
      // disc left showing (phone width -> desktop width, e.g. a tablet turned)
      // floats loose down the page. Hide it until the slider comes back.
      if (badge) badge.style.display = 'none';
      setOver(0);
      row.removeEventListener('pointerdown', onDown);
      row.removeEventListener('pointermove', onMove);
      row.removeEventListener('pointerup', onUp);
      row.removeEventListener('pointercancel', onCancel);
      row.removeEventListener('click', onClick, true);
      row.removeEventListener('focusin', onFocus);
      row.removeEventListener('dragstart', stopImgDrag);
      if (resizeObs) resizeObs.disconnect();
      resizeObs = null;
    };

    const sync = () => (query.matches ? enable() : disable());
    if (query.addEventListener) query.addEventListener('change', sync);
    else if (query.addListener) query.addListener(sync);
    sync();
  };

  rows.forEach(setup);
})();
